import {canViewFootballAnalytics} from "../components/football-intelligence/analyticsAccess";
// src/pages/PlayerPage.jsx
import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PlayerPropsSection from "../components/PlayerPropsSection";
import { useAuth } from "../components/AuthGate";
import RecordedPlayerFallback from "../components/football-app/RecordedPlayerFallback";
import PlayerProfileView from "../components/football-app/PlayerProfileView";
import { usePlayerCore, useIntelligence } from "../components/football-app/playerData";
import { slugifyTeamName } from "../utils/slugify";
import "../components/football-app/football-app.css";

const TABS = ["overview", "statistics", "matches", "shots", "heatmap", "props"];

/**
 * Full player profile. Public data (API-Football) loads for everyone; the
 * PitchAPI layers load only for administrators, as before.
 */
export default function PlayerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const fixtureId = sp.get("fixture_id");
  const auth = useAuth();
  const isAdmin = !!auth?.isAdmin;
  const analyticsAllowed = canViewFootballAnalytics(isAdmin);
  const tabParam = sp.get("tab");
  const tab = TABS.includes(tabParam) ? tabParam : "overview";
  const [filters, setFilters] = useState({ last_n: 10 });
  const [compareId, setCompareId] = useState("");

  const core = usePlayerCore({ playerId: id, fixtureId });
  const intel = useIntelligence({ kind: "player", identity: id, enabled: analyticsAllowed && !!id, filters, compareId });

  const setTab = (next) => {
    const params = new URLSearchParams(sp);
    params.set("tab", next);
    setSp(params, { replace: true });
  };
  const clubHref = (teamId, name) => `/club/${teamId}/${slugifyTeamName(name)}`;

  if (!fixtureId) {
    return (
      <div className="fa">
        <div className="fa-wrap" style={{ paddingTop: 24 }}>
          <p className="fa-empty" role="status">Open a player from a match lineup to see their profile.</p>
        </div>
      </div>
    );
  }
  if (core.loading) {
    return (
      <div className="fa">
        <div className="fa-wrap" style={{ paddingTop: 24 }}>
          <div className="fa-skeleton" role="status" aria-label="Loading player" style={{ height: 130 }} />
          <div className="fa-skeleton" style={{ height: 220 }} />
        </div>
      </div>
    );
  }
  if (core.error || !core.summary?.player) {
    return (
      <div className="fa">
        <div className="fa-wrap" style={{ paddingTop: 24 }}>
          <button type="button" className="fa-back" onClick={() => navigate(-1)} aria-label="Go back">←</button>
          {intel.data?.status !== "AVAILABLE" && <p className="fa-empty" role="alert">{core.error || "Player not found."}</p>}
          <RecordedPlayerFallback enabled={analyticsAllowed} data={intel.data} filters={filters} onFilter={(key,value)=>setFilters(f=>({...f,[key]:value||undefined}))} />
        </div>
      </div>
    );
  }

  return (
    <PlayerProfileView
      summary={core.summary}
      games={core.games}
      providerFixtureId={core.providerFixtureId}
      fixtureId={fixtureId}
      teams={core.teams}
      tab={tab}
      onTab={setTab}
      isAdmin={isAdmin}
      analyticsAllowed={analyticsAllowed}
      intel={intel}
      filters={filters}
      onFilter={(key, value) => setFilters((f) => ({ ...f, [key]: value || undefined }))}
      compare={{ id: compareId, set: setCompareId }}
      onNavigate={navigate}
      clubHref={clubHref}
      renderProps={() => (
        <PlayerPropsSection fixtureId={fixtureId} homeTeam={core.teams.home} awayTeam={core.teams.away} initialSearch={core.summary?.player?.name || ""} restrictToPlayer />
      )}
    />
  );
}
